import { useMutation } from '@apollo/client/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@optools/components/ui/button';
import { Card } from '@optools/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@optools/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@optools/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@optools/components/ui/select';
import { Switch } from '@optools/components/ui/switch';
import { usePolling } from '@optools/hooks/usePolling';
import { UPDATE_BROADCAST } from '@optools/services/operator-gateway/btc/mutations';
import { BTC_GAME } from '@optools/services/operator-gateway/btc/queries';
import { BtcGameBroadcast } from '@optools/services/operator-gateway/btc/types';
import {
  FreeGameType,
  MediaState,
  MediaType,
  VideoColorSpace,
  VideoResolution,
} from '@optools/services/operator-gateway/types/generated/graphql';
import {
  broadcastFormSchema,
  BroadcastFormValues,
} from '@optools/tools/btc-tools/schemas/broadcast';
import { hasBroadcastUpdates } from '@optools/tools/btc-tools/utils/broadcasts';
import { formatEnumValue } from '@optools/utils/format';
import { capitalize, pick } from 'es-toolkit';
import { Archive, Circle, CircleDot, CircleHelp, X } from 'lucide-react';
import { useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';

import { BroadcastFormConfirmation } from './broadcast-form-confirmation';

const POLL_INTERVAL = 5000;
const MAX_POLL_ATTEMPTS = 10;

export interface BroadcastFormProps {
  broadcast: BtcGameBroadcast;
  date: string;
  gamePk: number;
}

export function BroadcastForm({ broadcast, date, gamePk }: BroadcastFormProps) {
  const { mediaId } = broadcast;
  const [updateBroadcast, updateBroadcastResult] = useMutation(UPDATE_BROADCAST, {
    onCompleted: ({ updateBroadcast }) => {
      const { reason, success } = updateBroadcast;
      if (!success) {
        toast.error('Broadcast Update Error', { description: reason, duration: Infinity });
      } else {
        startPolling();
      }
    },
  });

  const { isPolling, startPolling } = usePolling({
    interval: POLL_INTERVAL,
    maxAttempts: MAX_POLL_ATTEMPTS,
    onMaxAttemptsReached: () => {
      toast.warning('Broadcast Update Error', {
        description:
          'The broadcast update is taking longer than expected to process. Please wait and refresh the page to check the status.',
        duration: Infinity,
      });
    },
    onPollingCompleted: () => {
      form.reset(form.getValues());
      toast.success('Broadcast successfully updated');
    },
    query: BTC_GAME,
    shouldStopPolling: ({ game }) => {
      const targetBroadcast = game?.broadcasts.find((broadcast) => broadcast.mediaId === mediaId);
      if (targetBroadcast) {
        const hasUpdates = hasBroadcastUpdates(targetBroadcast, form.getValues());
        return hasUpdates;
      }
    },
    variables: { gamePk, scheduleDate: date },
  });

  const loading = isPolling || updateBroadcastResult.loading;

  const form = useForm<BroadcastFormValues>({
    defaultValues: pick(broadcast, [
      'availableForStreaming',
      'mvpdRequired',
      'mediaState',
      'colorSpace',
      'videoResolution',
      'freeGameStatuses',
    ]),
    disabled: loading,
    resolver: zodResolver(broadcastFormSchema),
  });

  const freeGameStatusesValue = useWatch({ control: form.control, name: 'freeGameStatuses' });

  const handleSubmit = (values: BroadcastFormValues) => {
    updateBroadcast({ variables: { mediaId, ...values } });
  };

  const formRef = useRef<HTMLFormElement | null>(null);

  return (
    <Form {...form}>
      <form
        ref={formRef}
        aria-label="edit-broadcast"
        className="relative space-y-12 pb-16"
        onReset={() => form.reset()}
        onSubmit={form.handleSubmit(handleSubmit)}>
        <section className="space-y-4">
          <Card>
            <FormField
              control={form.control}
              name="availableForStreaming"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex cursor-pointer items-center justify-between p-6">
                    <div>
                      <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold">
                        Available for Streaming{' '}
                        <Popover>
                          <PopoverTrigger aria-label="video resolution help">
                            <CircleHelp className="size-4" role="presentation" />
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-full sm:max-w-96" side="right">
                            <ul className="space-y-4">
                              {[
                                {
                                  description: (
                                    <>
                                      Broadcast will be available on MLB.TV.
                                      <br />
                                      Only feeds marked as true will be provisioned.
                                    </>
                                  ),
                                  title: 'True',
                                },
                                {
                                  description: 'Present for display purposes only',
                                  title: 'False',
                                },
                              ].map(({ description, title }) => (
                                <li key={title}>
                                  <strong className="block text-sm">{title}</strong>
                                  <span className="text-xs">{description}</span>
                                </li>
                              ))}
                            </ul>
                          </PopoverContent>
                        </Popover>
                      </h2>
                      <p className="text-sm font-light">
                        Determines whether a broadcast will be available on MLB.TV.
                      </p>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        disabled={field.disabled}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormLabel>
                </FormItem>
              )}
            />
          </Card>

          <Card>
            <FormField
              control={form.control}
              name="mediaState"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between p-6">
                  <div>
                    <FormLabel className="mb-1 text-lg font-semibold">Media State</FormLabel>
                    <p className="text-sm font-light">
                      Determines playback availability for clients
                    </p>
                  </div>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-32 capitalize" disabled={field.disabled}>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {[MediaState.ON, MediaState.OFF, MediaState.ARCHIVE].map((state) => (
                        <SelectItem key={state} value={state}>
                          <div className="flex items-center gap-2">
                            {
                              {
                                [MediaState.ARCHIVE]: <Archive />,
                                [MediaState.OFF]: <Circle />,
                                [MediaState.ON]: <CircleDot />,
                              }[state]
                            }
                            {capitalize(state)}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
          </Card>
          {/* TODO: add field back in when bin api changes are made
            <FormField
              control={form.control}
              name="availability"
              render={({ field }) => (
                <Card>
                  <FormItem className="flex items-center justify-between p-6">
                    <div>
                      <h2 className="mb-1 text-lg font-semibold">Availability</h2>
                      <p className="text-sm font-light">Description for availability option</p>
                    </div>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger
                          aria-label="broadcast availability"
                          className="w-max capitalize"
                          disabled={field.disabled}>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.values(BroadcastAvailability).map((option) => (
                          <SelectItem key={option} value={option}>
                            <div className="flex items-center gap-2">{formatEnumValue(option)}</div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                </Card>
              )}
            /> */}
        </section>

        {broadcast.mediaType === MediaType.VIDEO && (
          <section>
            <h2 className="mb-4 text-xl font-semibold">Video Options</h2>

            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <FormField
                  control={form.control}
                  name="videoResolution"
                  render={({ field }) => (
                    <FormItem className="flex flex-wrap items-center justify-between gap-4 p-6">
                      <div className="flex-3 basis-56">
                        <div className="mb-1 inline-flex items-center gap-2">
                          <FormLabel className="text-lg font-semibold">Video Resolution</FormLabel>
                          <Popover>
                            <PopoverTrigger aria-label="video resolution help">
                              <CircleHelp className="size-4" role="presentation" />
                            </PopoverTrigger>
                            <PopoverContent
                              align="start"
                              className="w-full sm:max-w-96"
                              side="right">
                              <ul className="space-y-4">
                                {[
                                  {
                                    description: '720p = 921,600 pixels ',
                                    title: VideoResolution.HD,
                                  },
                                  {
                                    description: '1080p = 2,073,600 pixels',
                                    title: VideoResolution.FHD,
                                  },
                                  {
                                    description: '(4K) 2160p = 8,294,400 pixels',
                                    title: VideoResolution.UHD,
                                  },
                                ].map(({ description, title }) => (
                                  <li key={title}>
                                    <strong className="block text-sm">{title}</strong>
                                    <span className="text-xs">{description}</span>
                                  </li>
                                ))}
                              </ul>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <p className="text-sm font-light">Determines the sharpness and quality</p>
                      </div>
                      <div className="flex-1 basis-32">
                        <Select value={field.value ?? ''} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger className="w-full" disabled={field.disabled}>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {[VideoResolution.HD, VideoResolution.FHD, VideoResolution.UHD].map(
                              (resolution) => (
                                <SelectItem key={resolution} value={resolution}>
                                  {resolution}
                                </SelectItem>
                              ),
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    </FormItem>
                  )}
                />
              </Card>
              <Card>
                <FormField
                  control={form.control}
                  name="colorSpace"
                  render={({ field }) => (
                    <FormItem className="flex flex-wrap items-center justify-between gap-4 p-6">
                      <div className="flex-3 basis-56">
                        <div className="mb-1 inline-flex items-center gap-2">
                          <FormLabel className="text-lg font-semibold">Color Space</FormLabel>
                          <Popover>
                            <PopoverTrigger aria-label="video resolution help">
                              <CircleHelp className="size-4" role="presentation" />
                            </PopoverTrigger>
                            <PopoverContent
                              align="start"
                              className="w-full sm:max-w-96"
                              side="right">
                              <ul className="space-y-4">
                                {[
                                  {
                                    description: 'Standard Dynamic Range',
                                    title: VideoColorSpace.SDR,
                                  },
                                  {
                                    description: 'High Dynamic Range',
                                    title: VideoColorSpace.HDR,
                                  },
                                ].map(({ description, title }) => (
                                  <li key={title}>
                                    <strong className="block text-sm">{title}</strong>
                                    <span className="text-xs">{description}</span>
                                  </li>
                                ))}
                              </ul>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <p className="text-sm font-light">
                          Determines the range of color and brightness
                        </p>
                      </div>
                      <div className="flex-1 basis-32">
                        <Select value={field.value ?? ''} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger className="w-full" disabled={field.disabled}>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {[VideoColorSpace.SDR, VideoColorSpace.HDR].map((colorSpace) => (
                              <SelectItem key={colorSpace} value={colorSpace}>
                                {colorSpace}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </FormItem>
                  )}
                />
              </Card>
            </div>
          </section>
        )}

        <section>
          <h2 className="mb-4 text-xl font-semibold">Free Game Statuses</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {Object.keys(FreeGameType).map((freeGameType, index) => {
              const hasStatus = freeGameStatusesValue?.some(
                (status) => status.freeGameType === freeGameType,
              );
              return hasStatus ? (
                <Card key={freeGameType} className="min-h-36 animate-fade-in">
                  <FormField
                    control={form.control}
                    name="freeGameStatuses"
                    render={({ field }) => {
                      const item = field.value.find((item) => item.freeGameType === freeGameType);
                      const checked = item?.['status'];
                      return (
                        <FormItem className="relative h-full">
                          <FormLabel className="flex h-full cursor-pointer flex-col items-start p-6">
                            <div className="flex w-full items-center justify-between gap-4">
                              <div>
                                <h3 className="mb-2 text-lg">{formatEnumValue(freeGameType)}</h3>
                                <p className="text-sm font-light">
                                  {item?.freeGameType === FreeGameType.FREE_GAME_OF_THE_DAY &&
                                    'Determines whether the game (and each feed) has been designated as the Free Game of the Day and is available to anyone with a login (no subscription required)'}
                                  {item?.freeGameType === FreeGameType.FREE_GAME_EXTRA_INNINGS &&
                                    'Determines whether a game has gone to extra innings and the Video feeds have become free (as part of Geico Extra Innings)'}
                                </p>
                              </div>
                              <FormControl>
                                <Switch
                                  checked={checked}
                                  disabled={field.disabled}
                                  onCheckedChange={(value) => {
                                    const updatedStatuses = field.value.map((item) => {
                                      return item.freeGameType === freeGameType
                                        ? { ...item, status: value }
                                        : item;
                                    });

                                    field.onChange(updatedStatuses);
                                  }}
                                />
                              </FormControl>
                            </div>
                            {!broadcast.freeGameStatuses.some(
                              (status) => status.freeGameType === freeGameType,
                            ) && (
                              <Button
                                aria-label="Remove Status"
                                className="absolute top-2 right-2 size-6"
                                disabled={loading}
                                size="icon-sm"
                                type="button"
                                variant="outline"
                                onClick={() => {
                                  const updatedStatuses = freeGameStatusesValue.filter((item) => {
                                    return item.freeGameType !== freeGameType;
                                  });

                                  field.onChange(updatedStatuses);
                                }}>
                                <X />
                              </Button>
                            )}
                          </FormLabel>
                        </FormItem>
                      );
                    }}
                  />
                </Card>
              ) : (
                <div
                  key={freeGameType}
                  className="flex min-h-36 animate-fade-in flex-col items-center justify-center gap-4 rounded border border-dashed p-6"
                  data-testid={`placeholder-${freeGameType}`}>
                  <span>
                    No status for{' '}
                    <strong className="font-bold">{formatEnumValue(freeGameType)}</strong>
                  </span>
                  <Button
                    disabled={loading}
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const newStatus = {
                        freeGameType: freeGameType as FreeGameType,
                        status: true,
                      };
                      const statuses = form.getValues('freeGameStatuses') || [];
                      void (index > 0 ? statuses.push(newStatus) : statuses.unshift(newStatus));

                      form.setValue('freeGameStatuses', statuses, { shouldDirty: true });
                    }}>
                    Add Status
                  </Button>
                </div>
              );
            })}
          </div>
        </section>

        <Card>
          <FormField
            control={form.control}
            name="mvpdRequired"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex cursor-pointer items-center justify-between p-6">
                  <div>
                    <h2 className="mb-1 text-lg font-semibold">MVPD Required</h2>
                    <p className="text-sm font-light lg:max-w-3/4">
                      Determines whether MVPD authentication is required for a user in addition to
                      their MLB.TV subscription in order to watch the broadcast. This typically
                      applies to ASG and Postseason.
                    </p>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      disabled={field.disabled}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormLabel>
              </FormItem>
            )}
          />
        </Card>

        <BroadcastFormConfirmation
          hidden={!form.formState.isDirty}
          loading={loading}
          onConfirm={() => formRef.current?.requestSubmit()}
        />
      </form>
    </Form>
  );
}
